import { useContext, useMemo, useRef } from 'react';
import { ApiContext } from '@api/api';
import { Callbacks } from '@api/apiRuntime';
import {
  DryRunInDTO,
  DryRunOutDTO,
  FormatRuleInDTO,
  FormatRuleOutDTO,
  RuleDetailOutDTO,
  RuleListItemOutDTO,
  SaveRuleInDTO,
  SaveRuleOutDTO,
  ExecutionDetailOutDTO,
  PageResponseExecutionListItemOutDTO,
  PageResponsePlannedEventOutDTO,
  Pageable,
  RobotActivityFeedOutDTO,
  RobotActivityOutDTO,
  ValidateRuleInDTO,
  ValidateRuleOutDTO,
} from '@api/ringil-api.v2';

/**
 * One rule API for both sections.
 *
 * The company endpoints take the company from the logged-in user; the admin ones take it as a
 * query parameter. Choosing between the two sets happens here and nowhere else, so every component
 * just calls `listRules` / `saveRule` / … and passes `companyId` or not.
 *
 * `GET /metadata` is deliberately absent: the vocabulary does not vary by company, so there is
 * only one of it (see `RuleMetadataProvider`).
 */
export interface RuleApi {
  listRules: (onSuccess: (data: RuleListItemOutDTO[]) => void, callbacks?: Callbacks) => void;
  getRule: (ruleId: string, onSuccess: (data: RuleDetailOutDTO) => void, callbacks?: Callbacks) => void;
  /** Create when `ruleId` is undefined, update otherwise — the caller does not branch. */
  saveRule: (ruleId: string | undefined, body: SaveRuleInDTO, onSuccess: (data: SaveRuleOutDTO) => void, callbacks?: Callbacks) => void;
  deleteRule: (ruleId: string, onSuccess: () => void, callbacks?: Callbacks) => void;
  setRuleEnabled: (ruleId: string, enabled: boolean, onSuccess: (data: RuleDetailOutDTO) => void, callbacks?: Callbacks) => void;

  validate: (body: ValidateRuleInDTO, onSuccess: (data: ValidateRuleOutDTO) => void, callbacks?: Callbacks) => void;

  activity: (onSuccess: (data: RobotActivityOutDTO) => void, callbacks?: Callbacks) => void;
  /** `since` is the `at` of the newest item already held, so a poll usually returns nothing. */
  activityFeed: (since: string | undefined, limit: number, onSuccess: (data: RobotActivityFeedOutDTO) => void, callbacks?: Callbacks) => void;
  listExecutions: (page: Pageable, ruleId: string | undefined, onlyMatched: boolean | undefined, onSuccess: (data: PageResponseExecutionListItemOutDTO) => void, callbacks?: Callbacks) => void;
  getExecution: (executionId: string, onSuccess: (data: ExecutionDetailOutDTO) => void, callbacks?: Callbacks) => void;
  listPlanned: (page: Pageable, onSuccess: (data: PageResponsePlannedEventOutDTO) => void, callbacks?: Callbacks) => void;
  format: (body: FormatRuleInDTO, onSuccess: (data: FormatRuleOutDTO) => void, callbacks?: Callbacks) => void;
  dryRun: (body: DryRunInDTO, onSuccess: (data: DryRunOutDTO) => void, callbacks?: Callbacks) => void;
}

/** @param companyId undefined for the company section, the chosen company for the admin section. */
export const useRuleApi = (companyId?: string): RuleApi => {
  const api = useContext(ApiContext);

  /**
   * `ApiContext` hands out a fresh object on every render of its provider (`value={{ ...api }}`),
   * so memoising on it would rebuild this surface every render — and every hook that depends on
   * one of these functions would re-run its effects forever, which for a polling screen means a
   * request loop rather than a poll. The functions themselves never change behaviour, so the
   * latest object is kept in a ref and the surface is rebuilt only when the company does.
   */
  const apiRef = useRef(api);
  apiRef.current = api;

  return useMemo(() => {
    const admin = Boolean(companyId);
    // Read through the ref at call time, never captured, so a rebuilt context is picked up.

    return {
      listRules: (onSuccess, callbacks) => (admin ? apiRef.current.adminListRules(companyId, onSuccess, null, callbacks) : apiRef.current.listRules(onSuccess, null, callbacks)),

      getRule: (ruleId, onSuccess, callbacks) => (admin ? apiRef.current.adminGetRule(companyId, ruleId, onSuccess, null, callbacks) : apiRef.current.getRule(ruleId, onSuccess, null, callbacks)),

      saveRule: (ruleId, body, onSuccess, callbacks) => {
        if (ruleId) {
          return admin ? apiRef.current.adminUpdateRule(companyId, ruleId, body, onSuccess, null, callbacks) : apiRef.current.updateRule(ruleId, body, onSuccess, null, callbacks);
        }
        return admin ? apiRef.current.adminCreateRule(companyId, body, onSuccess, null, callbacks) : apiRef.current.createRule(body, onSuccess, null, callbacks);
      },

      deleteRule: (ruleId, onSuccess, callbacks) =>
        admin ? apiRef.current.adminDeleteRule(companyId, ruleId, onSuccess, null, callbacks) : apiRef.current.deleteRule(ruleId, onSuccess, null, callbacks),

      setRuleEnabled: (ruleId, enabled, onSuccess, callbacks) => {
        if (enabled) {
          return admin ? apiRef.current.adminEnableRule(companyId, ruleId, onSuccess, null, callbacks) : apiRef.current.enableRule(ruleId, onSuccess, null, callbacks);
        }
        return admin ? apiRef.current.adminDisableRule(companyId, ruleId, onSuccess, null, callbacks) : apiRef.current.disableRule(ruleId, onSuccess, null, callbacks);
      },

      activity: (onSuccess, callbacks) => (admin ? apiRef.current.adminActivity(companyId, onSuccess, null, callbacks) : apiRef.current.activity(onSuccess, null, callbacks)),
      activityFeed: (since, limit, onSuccess, callbacks) =>
        admin ? apiRef.current.adminActivityFeed(companyId, since, limit, onSuccess, null, callbacks) : apiRef.current.activityFeed(since, limit, onSuccess, null, callbacks),
      listExecutions: (page, ruleId, onlyMatched, onSuccess, callbacks) =>
        admin
          ? apiRef.current.adminListExecutions(companyId, page, ruleId, onlyMatched, onSuccess, null, callbacks)
          : apiRef.current.listExecutions(page, ruleId, onlyMatched, onSuccess, null, callbacks),
      getExecution: (executionId, onSuccess, callbacks) =>
        admin ? apiRef.current.adminGetExecution(companyId, executionId, onSuccess, null, callbacks) : apiRef.current.getExecution(executionId, onSuccess, null, callbacks),
      listPlanned: (page, onSuccess, callbacks) => (admin ? apiRef.current.adminListPlanned(companyId, page, onSuccess, null, callbacks) : apiRef.current.listPlanned(page, onSuccess, null, callbacks)),

      validate: (body, onSuccess, callbacks) => (admin ? apiRef.current.adminValidateRule(companyId, body, onSuccess, null, callbacks) : apiRef.current.validateRule(body, onSuccess, null, callbacks)),
      format: (body, onSuccess, callbacks) => (admin ? apiRef.current.adminFormatRule(companyId, body, onSuccess, null, callbacks) : apiRef.current.formatRule(body, onSuccess, null, callbacks)),
      dryRun: (body, onSuccess, callbacks) => (admin ? apiRef.current.adminDryRunRule(companyId, body, onSuccess, null, callbacks) : apiRef.current.dryRunRule(body, onSuccess, null, callbacks)),
    };
  }, [companyId]);
};
